import {
  FOC_TOUR_LABEL_SITL_COP,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import { FOC_REQUIRED_DISPOSITION } from '../orgWorkspaceTour/orgWorkspaceTourSteps';
import { SITL_SEAT_SLUG } from '../utils/mapExclusiveSurfaces';

export const SITL_COP_TOUR_PARAM = 'sitlCopTour';

/** Hub bootstrap: resolve an incident, then open the SITL seat tour. */
export const SITL_COP_TOUR_HREF = `/hub?${SITL_COP_TOUR_PARAM}=1`;

export function sitlCopTourSeatHref(incidentId: string): string {
  return `/iap/${incidentId}/seat/${SITL_SEAT_SLUG}?${SITL_COP_TOUR_PARAM}=1`;
}

export type SitlCopTourStepId =
  | 'sectionIntro'
  | 'cop001'
  | 'cop001a'
  | 'cop001b'
  | 'cop001c'
  | 'cop002'
  | 'cop002a'
  | 'cop002b'
  | 'cop012Brief'
  | 'cop002c'
  | 'cop003'
  | 'cop004'
  | 'cop005'
  | 'focFeedback';

export type SitlCopTourStepDef = {
  id: SitlCopTourStepId;
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  bullets?: string[];
  tour?: string;
};

type RequirementInput = Omit<SitlCopTourStepDef, 'title' | 'requirementId'> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): SitlCopTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

export const SITL_COP_TOUR_STEPS: SitlCopTourStepDef[] = [
  {
    id: 'sectionIntro',
    tour: 'sitl-cop-workspace',
    title: '5.3 Incident-Level SITL COP Workspace',
    description:
      'The Situation Unit Leader workspace is a dedicated incident-level Common Operating Picture mode, separate from the general Hub view, for creating, revising, staging, briefing from, and publishing tactical incident map products.',
  },
  requirement({
    id: 'cop001',
    requirementId: 'FOC-COP-001',
    name: 'Dedicated SITL COP Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'sitl-cop-chrome',
    description:
      'PRATUS provides a dedicated incident-level Situation Unit Leader COP workspace or mode separate from the general Hub view. The workspace enables the SITL and authorized contributors to create, revise, stage, brief from, and publish tactical incident map products without unintentionally changing the live operational COP.',
  }),
  requirement({
    id: 'cop001a',
    requirementId: 'FOC-COP-001a',
    name: 'Incident-Focused Toolkit',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'sitl-cop-toolkit',
    description:
      'The SITL workspace provides direct access to incident-focused mapping tools, common incident features, map annotations, ICS symbology, address/coordinate entry, incident overlays, and briefing-ready cartographic elements.',
  }),
  requirement({
    id: 'cop001b',
    requirementId: 'FOC-COP-001b',
    name: 'Non-GIS-Specialist Usability',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'sitl-cop-open-sitstat',
    description:
      'A qualified SITL or authorized incident user can create a basic briefing-ready map without contractor support or expert GIS intervention. Open SitStat and add an internal layer with guided tools.',
  }),
  requirement({
    id: 'cop001c',
    requirementId: 'FOC-COP-001c',
    name: 'Advanced GIS Path',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'sitl-cop-open-layers',
    description:
      'PRATUS retains access to advanced ArcGIS and GIS functions for authorized users without requiring routine incident users to navigate advanced GIS functions to create, update, or brief standard incident maps.',
  }),
  requirement({
    id: 'cop002',
    requirementId: 'FOC-COP-002',
    name: 'Draft, Save, and Publish Model',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'hub-cop-review-queue',
    description:
      'PRATUS provides draft, save, review, approval, publish, and revision controls for incident COP views, map annotations, layers, and briefing products.',
  }),
  requirement({
    id: 'cop002a',
    requirementId: 'FOC-COP-002a',
    name: 'Draft Protection',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'hub-layers-workspace',
    description:
      'Draft map work does not automatically alter the live operational map or a published incident/organizational COP.',
  }),
  requirement({
    id: 'cop002b',
    requirementId: 'FOC-COP-002b',
    name: 'Saved Briefing Views',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'sitl-cop-open-presentation',
    description:
      'Authorized users can save multiple map views and Presentations for different operational periods, briefings, audiences, or incident-management purposes. Open Presentation from the Situation Unit chrome to build a briefing deck without leaving the SITL workspace.',
  }),
  requirement({
    id: 'cop012Brief',
    requirementId: 'FOC-COP-012',
    name: 'Situation Display / Briefing Board',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'sitl-presentation-workspace',
    description:
      'PRATUS provides a Situation Unit Leader-managed Presentation that presents the most decision-relevant incident information in a compact, briefing-ready format and can be updated as conditions change without requiring the SITL to leave the briefing context for routine corrections. A fuller walkthrough of configurable content and product identity is in product tour 5.6.',
  }),
  requirement({
    id: 'cop002c',
    requirementId: 'FOC-COP-002c',
    name: 'Publication Authority',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'hub-cop-review-queue',
    description:
      'PRATUS applies role-based controls to identify who can create drafts, edit drafts, propose publication, approve publication, publish, revert, and view incident COP products.',
  }),
  requirement({
    id: 'cop003',
    requirementId: 'FOC-COP-003',
    name: 'Tactical Drawing and Feature Tools',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'hub-cop-symbology',
    description:
      'Authorized users can add, move, edit, and remove incident-relevant map features through an operationally usable interface, including IMH-aligned ICS symbols for posts, staging, branches, divisions, facilities, routes, work areas, exclusion zones, search areas, and resource locations.',
  }),
  requirement({
    id: 'cop004',
    requirementId: 'FOC-COP-004',
    name: 'Multiple COP Contributors',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'hub-cop-layer-detail',
    description:
      'PRATUS supports role-appropriate incident-map contributions by authorized Planning, Operations, Logistics, Facilities, and other incident personnel while preserving SITL integration and briefing control.',
  }),
  requirement({
    id: 'cop005',
    requirementId: 'FOC-COP-005',
    name: 'Baseline Cartographic and Briefing Elements',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'aor-map-view',
    description:
      'PRATUS provides briefing-ready map elements that include north arrow, distance/scale, legend, coordinate readout, labels, map title, operational period, layer key, and user controls to show, hide, or adjust these elements for briefing purposes.',
    bullets: [
      'Legend and layer key on the map and Layers panel',
      'Coordinate entry when placing features',
      'Map canvas for north arrow, scale, title, and period overlays as available',
    ],
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_SITL_COP),
  },
];

export function sitlCopTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function sitlCopTourStepIds(): SitlCopTourStepId[] {
  return SITL_COP_TOUR_STEPS.map((step) => step.id);
}

export function sitlCopTourStepTitles(): string[] {
  return SITL_COP_TOUR_STEPS.map((step) => step.title);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function sitlCopTourPopoverHtml(
  step: Pick<SitlCopTourStepDef, 'disposition' | 'description' | 'bullets'>
): string {
  const body = `<p>${escapeHtml(step.description)}</p>`;
  const bullets = step.bullets?.length
    ? `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    : '';
  const content = `${body}${bullets}`;
  if (!step.disposition) return content;
  return `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>${content}`;
}

/** Live incident candidate for hub → SITL seat bootstrap. */
export type SitlCopTourIncidentCandidate = {
  id: string;
  archived?: number | boolean | null;
};

export function resolveSitlCopTourSeatHref(
  currentId: string | undefined,
  incidents: SitlCopTourIncidentCandidate[]
): string | null {
  const live = incidents.filter((incident) => !incident.archived);
  const current = live.find((incident) => incident.id === currentId);
  const pick = current ?? live[0];
  return pick ? sitlCopTourSeatHref(pick.id) : null;
}
